const dietaryBusinessEventNamedRelation = (
	json: IDietaryBusinessEventNamedRelation,
): IDietaryBusinessEventNamedRelationModel => {
	const { id, code, name, internal_name, relation_type } = json

	return {
		id,
		name,
		...(code !== undefined ? { code } : {}),
		...(internal_name !== undefined ? { internalName: internal_name } : {}),
		...(relation_type !== undefined ? { relationType: relation_type } : {}),
	}
}

const dietaryBusinessEventNutritionalParameter = (
	json: IDietaryBusinessEventNutritionalParameter,
): IDietaryBusinessEventNutritionalParameterModel => {
	const { id, code, name, short_name, unit, value } = json

	return { id, code, name, unit, value, shortName: short_name }
}

const dietaryBusinessEventRawMaterialAlternative = (
	json: IDietaryBusinessEventRawMaterialAlternative,
): IDietaryBusinessEventRawMaterialAlternativeModel => {
	const {
		id,
		raw_material_id,
		raw_material_name,
		alternative_raw_material_id,
		alternative_raw_material_name,
		alternative_raw_material_internal_name,
		net_weight,
		auto_sync,
		order,
	} = json

	return {
		id,
		order,
		rawMaterialID: raw_material_id,
		rawMaterialName: raw_material_name,
		alternativeRawMaterialID: alternative_raw_material_id,
		alternativeRawMaterialName: alternative_raw_material_name,
		alternativeRawMaterialInternalName: alternative_raw_material_internal_name,
		netWeight: net_weight,
		autoSync: auto_sync,
	}
}

const dietaryBusinessEventComponent = (json: IDietaryBusinessEventComponent): IDietaryBusinessEventComponentModel => {
	const {
		id,
		type,
		name,
		internal_name,
		raw_weight,
		weight_after_processing,
		weight_after_processing_ep,
		weight_after_processing_mech_ep,
		net_weight,
		weight_before_wom,
		wom,
		net_weight_per_piece,
		number_of_pieces,
		weight_unit,
		group_id,
		order,
	} = json

	return {
		id,
		type,
		name,
		internalName: internal_name,
		rawWeight: raw_weight,
		weightAfterProcessing: weight_after_processing,
		weightAfterProcessingEp: weight_after_processing_ep,
		weightAfterProcessingMechEp: weight_after_processing_mech_ep,
		netWeight: net_weight,
		weightBeforeWom: weight_before_wom,
		wom,
		...(net_weight_per_piece !== undefined ? { netWeightPerPiece: net_weight_per_piece } : {}),
		...(number_of_pieces !== undefined ? { numberOfPieces: number_of_pieces } : {}),
		weightUnit: weight_unit,
		groupID: group_id,
		order,
	}
}

const dietaryBusinessEventCutting = (json: IDietaryBusinessEventCutting): IDietaryBusinessEventCuttingModel => {
	const { id, cutting_type, cutting_type_label } = json

	return { id, cuttingType: cutting_type, cuttingTypeLabel: cutting_type_label }
}

const dietaryBusinessEventQualityControlParameter = (
	json: IDietaryBusinessEventQualityControlParameter,
): IDietaryBusinessEventQualityControlParameterModel => {
	const { id, name, value } = json

	return { id, name, value }
}

const dietaryBusinessEventComponentWot = (
	json: IDietaryBusinessEventComponentWot,
): IDietaryBusinessEventComponentWotModel => {
	const { model_type, model_id, model_name, weight_percentage_after_stage } = json

	return {
		modelType: model_type,
		modelID: model_id,
		modelName: model_name,
		weightPercentageAfterStage: weight_percentage_after_stage,
	}
}

const dietaryBusinessEventProductionStageAssignment = (
	json: IDietaryBusinessEventProductionStageAssignment,
): IDietaryBusinessEventProductionStageAssignmentModel => {
	const {
		id,
		order,
		production_stage_id,
		production_stage_name,
		production_socket_id,
		production_socket_name,
		duration,
		quantity_by_time,
		description,
		weight_percentage_after_stage,
		weight_waste,
		model_id,
		model_type,
		cutting,
		machine_types,
		quality_control_parameters,
		component_wots,
	} = json

	return {
		id,
		order,
		duration,
		description,
		productionStageID: production_stage_id,
		productionStageName: production_stage_name,
		productionSocketID: production_socket_id,
		productionSocketName: production_socket_name,
		quantityByTime: quantity_by_time,
		weightPercentageAfterStage: weight_percentage_after_stage,
		weightWaste: weight_waste,
		modelID: model_id,
		modelType: model_type,
		cutting: cutting ? dietaryBusinessEventCutting(cutting) : null,
		machineTypes: machine_types.map(dietaryBusinessEventNamedRelation),
		qualityControlParameters: quality_control_parameters.map(dietaryBusinessEventQualityControlParameter),
		componentWots: component_wots.map(dietaryBusinessEventComponentWot),
	}
}

const dietaryBusinessEventSemiProductGroup = (
	json: IDietaryBusinessEventSemiProductGroup,
): IDietaryBusinessEventSemiProductGroupModel => {
	const { id, name, production_stage_assignments } = json

	return {
		id,
		name,
		productionStageAssignments: production_stage_assignments.map(dietaryBusinessEventProductionStageAssignment),
	}
}

const dietaryBusinessEventSemiProductAlternative = (
	json: IDietaryBusinessEventSemiProductAlternative,
): IDietaryBusinessEventSemiProductAlternativeModel => {
	const {
		id,
		original_raw_material_id,
		original_raw_material_name,
		alternative_raw_material_id,
		alternative_raw_material_name,
		group_id,
		net_weight,
		order,
	} = json

	return {
		id,
		order,
		originalRawMaterialID: original_raw_material_id,
		originalRawMaterialName: original_raw_material_name,
		alternativeRawMaterialID: alternative_raw_material_id,
		alternativeRawMaterialName: alternative_raw_material_name,
		groupID: group_id,
		netWeight: net_weight,
	}
}

const dietaryBusinessEventNutriHex = (json: IDietaryBusinessEventNutriHex): IDietaryBusinessEventNutriHexModel => {
	const { id, glycemic_index, glycemic_load, protein, fiber, simple_sugars, salt } = json

	return {
		id,
		protein,
		fiber,
		salt,
		glycemicIndex: glycemic_index,
		glycemicLoad: glycemic_load,
		simpleSugars: simple_sugars,
	}
}

const dietaryBusinessEventVariantNutriHex = (
	json: IDietaryBusinessEventVariantNutriHex,
): IDietaryBusinessEventVariantNutriHexModel => {
	const { variant_id, ...points } = json

	return { variantID: variant_id, ...dietaryBusinessEventNutriHex(points) }
}

const dietaryBusinessEventPackagingItem = (
	json: IDietaryBusinessEventPackagingItem,
): IDietaryBusinessEventPackagingItemModel => {
	const { id, model_type, model_id, model_name, compartment, order, description } = json

	return {
		id,
		compartment,
		order,
		description,
		modelType: model_type,
		modelID: model_id,
		modelName: model_name,
	}
}

const dietaryBusinessEventVariantPackaging = (
	json: IDietaryBusinessEventVariantPackaging,
): IDietaryBusinessEventVariantPackagingModel => {
	const {
		variant_id,
		id,
		meal_packaging_type_id,
		meal_packaging_type_name,
		can_produce_separately,
		weight,
		is_salad,
		oxygen,
		items,
	} = json

	return {
		id,
		weight,
		oxygen,
		variantID: variant_id,
		mealPackagingTypeID: meal_packaging_type_id,
		mealPackagingTypeName: meal_packaging_type_name,
		canProduceSeparately: can_produce_separately,
		isSalad: is_salad,
		items: items.map(dietaryBusinessEventPackagingItem),
	}
}

const dietaryBusinessEventMedia = (json: IDietaryBusinessEventMedia): IDietaryBusinessEventMediaModel => {
	const { id, name, file_name } = json

	return { id, name, fileName: file_name }
}

const dietaryBusinessEventSizeMatrix = (
	json: IDietaryBusinessEventSizeMatrix,
): IDietaryBusinessEventSizeMatrixModel => {
	const { id, meal_type, meal_type_label, meal_size_id, meal_size_code, meal_size_label, kcal } = json

	return {
		id,
		kcal,
		mealType: meal_type,
		mealTypeLabel: meal_type_label,
		mealSizeID: meal_size_id,
		mealSizeCode: meal_size_code,
		mealSizeLabel: meal_size_label,
	}
}

const dietaryBusinessEventMealType = (json: IDietaryBusinessEventMealType): IDietaryBusinessEventMealTypeModel => {
	const { meal_type, meal_type_label, meal_count } = json

	return { mealType: meal_type, mealTypeLabel: meal_type_label ?? null, mealCount: meal_count }
}

const dietaryBusinessEventMealVariant = (
	json: IDietaryBusinessEventMealVariant,
): IDietaryBusinessEventMealVariantModel => {
	const {
		id,
		meal_size_id,
		meal_size_code,
		meal_size_label,
		preparation_time,
		is_active,
		has_same_components,
		ingredients,
		margin,
		margin_gross,
		margin_amount,
		margin_amount_gross,
		cost,
		cost_gross,
		nutrihex_points,
		raw_materials,
		semi_products,
	} = json

	return {
		id,
		ingredients,
		margin,
		cost,
		mealSizeID: meal_size_id,
		mealSizeCode: meal_size_code,
		mealSizeLabel: meal_size_label,
		preparationTime: preparation_time,
		isActive: is_active,
		hasSameComponents: has_same_components,
		marginGross: margin_gross,
		marginAmount: margin_amount,
		marginAmountGross: margin_amount_gross,
		costGross: cost_gross,
		nutrihexPoints: nutrihex_points ? dietaryBusinessEventNutriHex(nutrihex_points) : null,
		rawMaterials: raw_materials.map(dietaryBusinessEventComponent),
		semiProducts: semi_products.map(dietaryBusinessEventComponent),
	}
}

const dietaryBusinessEventDietDayMeal = (
	json: IDietaryBusinessEventDietDayMeal,
): IDietaryBusinessEventDietDayMealModel => {
	const { id, name } = json

	return { id, name: getDefaultTranslation(name) }
}

const dietaryBusinessEventDietDayVariant = (
	json: IDietaryBusinessEventDietDayVariant,
): IDietaryBusinessEventDietDayVariantModel => {
	const { id, meal_id, meal_name, kcal, meal_type, meal_type_label, meal_kind } = json

	return {
		id,
		kcal,
		mealID: meal_id,
		mealName: meal_name,
		mealType: meal_type,
		mealTypeLabel: meal_type_label,
		mealKind: meal_kind,
	}
}

const DIETARY_SEMI_PRODUCT_SUBJECT_TYPES = new Set(['dietary_semi_product', 'semi_product'])

const isNullableNumber = (value: unknown): value is number | null => typeof value === 'number' || value === null

const isNullableString = (value: unknown): value is string | null => typeof value === 'string' || value === null

const isDietaryBusinessEventProductionStageAssignment = (
	value: unknown,
): value is IDietaryBusinessEventProductionStageAssignment => {
	if (typeof value !== 'object' || value === null) return false

	return (
		typeof (value as IDietaryBusinessEventProductionStageAssignment).id === 'number' &&
		isNullableNumber((value as IDietaryBusinessEventProductionStageAssignment).production_stage_id) &&
		isNullableString((value as IDietaryBusinessEventProductionStageAssignment).production_stage_name) &&
		isNullableNumber((value as IDietaryBusinessEventProductionStageAssignment).production_socket_id) &&
		isNullableString((value as IDietaryBusinessEventProductionStageAssignment).production_socket_name) &&
		Array.isArray((value as IDietaryBusinessEventProductionStageAssignment).machine_types) &&
		Array.isArray((value as IDietaryBusinessEventProductionStageAssignment).quality_control_parameters) &&
		Array.isArray((value as IDietaryBusinessEventProductionStageAssignment).component_wots)
	)
}

const isDietaryBusinessEventSemiProductGroup = (value: unknown): value is IDietaryBusinessEventSemiProductGroup => {
	if (typeof value !== 'object' || value === null) return false

	const group = value as IDietaryBusinessEventSemiProductGroup

	return (
		typeof group.id === 'number' &&
		isNullableString(group.name) &&
		Array.isArray(group.production_stage_assignments) &&
		group.production_stage_assignments.every(isDietaryBusinessEventProductionStageAssignment)
	)
}

const isDietaryBusinessEventVariantNutriHex = (value: unknown): value is IDietaryBusinessEventVariantNutriHex =>
	typeof value === 'object' &&
	value !== null &&
	typeof (value as IDietaryBusinessEventVariantNutriHex).variant_id === 'number'

const isSupportedDietaryBusinessEventStructuredValue = (
	field: string,
	value: TDietaryBusinessEventChangeValue,
	subjectType: string | null,
): boolean => {
	if (value === null) return true

	switch (field) {
		case 'dietary_groups':
			return (
				DIETARY_SEMI_PRODUCT_SUBJECT_TYPES.has(subjectType ?? '') &&
				Array.isArray(value) &&
				value.every(isDietaryBusinessEventSemiProductGroup)
			)
		case 'production_stage_assignments':
			return Array.isArray(value) && value.every(isDietaryBusinessEventProductionStageAssignment)
		case 'variants_nutrihex_points':
			return Array.isArray(value) && value.every(isDietaryBusinessEventVariantNutriHex)
		default:
			return true
	}
}

const DIETARY_NAMED_RELATION_FIELDS = new Set([
	'allergens',
	'addon_category',
	'allergen_exclusions',
	'dietary_tags',
	'dietary_tag_exclusions',
	'filter_tags',
	'marketing_tags',
	'meal_size_exclusions',
	'related_meals',
])

const mapDietaryBusinessEventAdditionalArray = (
	field: string,
	value: Exclude<TDietaryBusinessEventChangeValue, TDietaryBusinessEventScalarValue>,
): TDietaryBusinessEventChangeModelValue => {
	switch (field) {
		case 'variants_nutrihex_points':
			return (value as IDietaryBusinessEventVariantNutriHex[]).map(dietaryBusinessEventVariantNutriHex)
		case 'variants_meal_packaging':
			return (value as IDietaryBusinessEventVariantPackaging[]).map(dietaryBusinessEventVariantPackaging)
		case 'client_photos':
		case 'kitchen_photos':
			return (value as IDietaryBusinessEventMedia[]).map(dietaryBusinessEventMedia)
		case 'size_matrices':
			return (value as IDietaryBusinessEventSizeMatrix[]).map(dietaryBusinessEventSizeMatrix)
		case 'meal_types':
			return (value as IDietaryBusinessEventMealType[]).map(dietaryBusinessEventMealType)
		default:
			return value as TDietaryBusinessEventJson[]
	}
}

const mapDietaryBusinessEventStructuredArray = (
	field: string,
	value: Exclude<TDietaryBusinessEventChangeValue, TDietaryBusinessEventScalarValue>,
	subjectType: string | null,
): TDietaryBusinessEventChangeModelValue => {
	if (DIETARY_NAMED_RELATION_FIELDS.has(field)) {
		return (value as IDietaryBusinessEventNamedRelation[]).map(dietaryBusinessEventNamedRelation)
	}

	switch (field) {
		case 'nutritional_parameters':
			return (value as IDietaryBusinessEventNutritionalParameter[]).map(dietaryBusinessEventNutritionalParameter)
		case 'raw_material_alternatives':
			return (value as IDietaryBusinessEventRawMaterialAlternative[]).map(
				dietaryBusinessEventRawMaterialAlternative,
			)
		case 'raw_materials':
		case 'semi_products':
		case 'components':
			return (value as IDietaryBusinessEventComponent[]).map(dietaryBusinessEventComponent)
		case 'production_stage_assignments':
			return (value as IDietaryBusinessEventProductionStageAssignment[]).map(
				dietaryBusinessEventProductionStageAssignment,
			)
		case 'dietary_groups':
			return (value as IDietaryBusinessEventSemiProductGroup[]).map(dietaryBusinessEventSemiProductGroup)
		case 'raw_material_component_alternatives':
			return (value as IDietaryBusinessEventSemiProductAlternative[]).map(
				dietaryBusinessEventSemiProductAlternative,
			)
		case 'variants':
			return subjectType === 'diet_day'
				? (value as IDietaryBusinessEventDietDayVariant[]).map(dietaryBusinessEventDietDayVariant)
				: (value as IDietaryBusinessEventMealVariant[]).map(dietaryBusinessEventMealVariant)
		case 'meals':
			return (value as IDietaryBusinessEventDietDayMeal[]).map(dietaryBusinessEventDietDayMeal)
		default:
			return mapDietaryBusinessEventAdditionalArray(field, value)
	}
}

const mapDietaryBusinessEventChangeValue = (
	field: string,
	value: TDietaryBusinessEventChangeValue,
	subjectType: string | null,
): TDietaryBusinessEventChangeModelValue => {
	if (value === null || typeof value !== 'object') {
		return value
	}

	if (Array.isArray(value)) {
		return mapDietaryBusinessEventStructuredArray(field, value, subjectType)
	}

	return field === 'nutrihex_points'
		? dietaryBusinessEventNutriHex(value as IDietaryBusinessEventNutriHex)
		: (value as { [key: string]: TDietaryBusinessEventJson })
}

const dietaryActivityBusinessEventChange = (
	json: IDietaryActivityBusinessEventChange,
	subjectType: string | null,
): IDietaryActivityBusinessEventChangeModel | null => {
	const { field, before, after } = json
	if (
		!isSupportedDietaryBusinessEventStructuredValue(field, before, subjectType) ||
		!isSupportedDietaryBusinessEventStructuredValue(field, after, subjectType)
	)
		return null

	return {
		field,
		before: mapDietaryBusinessEventChangeValue(field, before, subjectType),
		after: mapDietaryBusinessEventChangeValue(field, after, subjectType),
	}
}

const dietaryBusinessEventAffectedDietDay = (
	json: IDietaryBusinessEventAffectedDietDay,
): IDietaryBusinessEventAffectedDietDayModel => {
	const { id, date, diet_id, diet_name } = json

	return { id, date, dietID: diet_id, dietName: diet_name }
}

const dietaryBusinessEventRelatedUsage = (
	json: IDietaryBusinessEventRelatedUsage,
): IDietaryBusinessEventRelatedUsageModel => {
	const { used_by_meals, used_by_semi_products, affected_diet_days } = json

	return {
		usedByMeals: used_by_meals.map(dietaryBusinessEventNamedRelation),
		...(used_by_semi_products
			? { usedBySemiProducts: used_by_semi_products.map(dietaryBusinessEventNamedRelation) }
			: {}),
		affectedDietDays: affected_diet_days.map(dietaryBusinessEventAffectedDietDay),
	}
}

const dietaryBusinessEventTrigger = (json: IDietaryBusinessEventTrigger): IDietaryBusinessEventTriggerModel => {
	const { entity_type, entity_id, name } = json

	return { entityType: entity_type, entityID: entity_id, name }
}

const dietaryBusinessEventProductionPlan = (
	json: IDietaryBusinessEventProductionPlan,
): IDietaryBusinessEventProductionPlanModel => {
	const { date, variant_id, quantity, quantity_used, is_fridge } = json

	return {
		date,
		quantity,
		variantID: variant_id,
		quantityUsed: quantity_used,
		isFridge: is_fridge,
	}
}

const isDietaryBusinessEventRelatedUsageSnapshot = (
	value: IDietaryBusinessEventRelatedUsage | IDietaryBusinessEventRelatedUsageSnapshot,
): value is IDietaryBusinessEventRelatedUsageSnapshot => 'before' in value && 'after' in value

const dietaryActivityBusinessEventPayload = (
	json: IDietaryActivityBusinessEventPayload,
	event: string,
): IDietaryActivityBusinessEventPayloadModel => {
	const { trigger, related_usage_snapshot, production_plan, meals, variants, ...rest } = json
	const mapped: IDietaryActivityBusinessEventPayloadModel = {}

	for (const [key, value] of Object.entries(rest)) {
		mapped[keyToCamelCase(key)] = value as TDietaryBusinessEventJson
	}

	if (event === 'production_plan_meal_updated' && production_plan) {
		mapped.productionPlan = dietaryBusinessEventProductionPlan(production_plan)
	}

	if ((event === 'diet_day_copied' || event === 'diet_day_published') && meals) {
		mapped.meals = meals.map(dietaryBusinessEventDietDayMeal)
	}

	if ((event === 'diet_day_copied' || event === 'diet_day_published') && variants) {
		mapped.variants = variants.map(dietaryBusinessEventDietDayVariant)
	}

	if ((event === 'raw_material_relations_changed' || event === 'semi_product_relations_changed') && trigger) {
		mapped.trigger = dietaryBusinessEventTrigger(trigger)
	}

	if (related_usage_snapshot) {
		mapped.relatedUsageSnapshot = isDietaryBusinessEventRelatedUsageSnapshot(related_usage_snapshot)
			? {
					before: dietaryBusinessEventRelatedUsage(related_usage_snapshot.before),
					after: dietaryBusinessEventRelatedUsage(related_usage_snapshot.after),
				}
			: dietaryBusinessEventRelatedUsage(related_usage_snapshot)
	}

	return mapped
}

const dietaryActivityBusinessEventCauser = (
	json: IDietaryActivityBusinessEventCauser,
): IDietaryActivityBusinessEventCauserModel => {
	const { id, first_name, last_name, email, roles } = json

	return { id, email, roles, firstName: first_name, lastName: last_name }
}

const dietaryBusinessEventStructuredSubjectType = (
	subjectType: string | null,
	payload: IDietaryActivityBusinessEventPayload,
): string | null => (payload.menu_context ? (payload.entity_type ?? subjectType) : subjectType)

export function DietaryActivityBusinessEvent(
	json: IDietaryProductionPlanMealUpdatedBusinessEvent,
): IDietaryProductionPlanMealUpdatedBusinessEventModel
export function DietaryActivityBusinessEvent(
	json: IDietaryDietDayCopiedBusinessEvent,
): IDietaryDietDayCopiedBusinessEventModel
export function DietaryActivityBusinessEvent(
	json: IDietaryDietDayPublishedBusinessEvent,
): IDietaryDietDayPublishedBusinessEventModel
export function DietaryActivityBusinessEvent(
	json: IDietaryRelationChangedBusinessEvent,
): IDietaryRelationChangedBusinessEventModel
export function DietaryActivityBusinessEvent(json: IDietaryActivityBusinessEvent): IDietaryActivityBusinessEventModel
export function DietaryActivityBusinessEvent(json: IDietaryActivityBusinessEvent): IDietaryActivityBusinessEventModel {
	const {
		id,
		entity_id,
		action,
		channel,
		log_name,
		date,
		editor,
		causer,
		description,
		event,
		changes,
		payload,
		subject_id,
		subject_type,
		for_client,
	} = json
	const structuredSubjectType = dietaryBusinessEventStructuredSubjectType(subject_type, payload)

	return {
		id,
		action,
		channel,
		editor,
		description,
		event,
		entityID: entity_id,
		logName: log_name,
		date: new Date(date),
		causer: causer ? dietaryActivityBusinessEventCauser(causer) : null,
		changes: (() => {
			const mappedChanges: IDietaryActivityBusinessEventChangeModel[] = []
			for (const change of changes) {
				const mappedChange = dietaryActivityBusinessEventChange(change, structuredSubjectType)
				if (mappedChange) {
					mappedChanges.push(mappedChange)
					continue
				}
				console.error(
					`Unsupported dietary business event structured snapshot dropped: log=${id}, event=${event}, field=${change.field}`,
				)
			}
			return mappedChanges
		})(),
		payload: dietaryActivityBusinessEventPayload(payload, event),
		subjectID: subject_id,
		subjectType: subject_type,
		forClient: for_client,
	}
}
